import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  X, Phone, ArrowLeft, Wind, Droplets, AlertTriangle, Layers, Camera,
  Search, FileText, Users, ClipboardCheck, Hammer, ShieldCheck, CheckCircle2,
} from "lucide-react";
import { COMPANY, IMAGES } from "@/data/content";
import Header from "@/components/site/Header";
import FAQ from "@/components/site/FAQ";
import CTABand from "@/components/site/CTABand";
import Footer from "@/components/site/Footer";
import StickyCallBar from "@/components/site/StickyCallBar";
import QuoteForm from "@/components/site/QuoteForm";

const SITE_URL = "https://honeybeeroofers.com";
const PAGE_PATH = "/insurance-roof-replacement";

const SIGNS = [
  { icon: Layers, title: "Missing or Torn Shingles", body: "Bare patches where wind has peeled shingles off the deck — the most obvious sign of storm damage." },
  { icon: Wind, title: "Lifted or Creased Shingles", body: "Wind breaks the adhesive seal and folds shingles back, leaving creases that let water underneath." },
  { icon: AlertTriangle, title: "Granule Loss & Bald Spots", body: "Piles of sandy granules in gutters or downspouts mean the protective surface is wearing off." },
  { icon: ShieldCheck, title: "Damaged or Missing Flashing", body: "Bent, lifted, or missing flashing around chimneys, vents, and valleys is a common leak point." },
  { icon: Droplets, title: "Leaks & Ceiling Water Stains", body: "Brown rings on ceilings, damp attic insulation, or daylight through the roof deck signal active intrusion." },
  { icon: AlertTriangle, title: "Debris & Fallen Limbs", body: "Branches, punctures, or dents from wind-driven debris and hail can compromise the whole system." },
];

const CHECKLIST = [
  "From the ground (stay safe — never climb a storm-damaged roof), scan for missing, curled, or crooked shingles.",
  "Walk your yard and driveway for shingle pieces, granules, or flashing that blew off.",
  "Check gutters and downspouts for granule buildup and dents.",
  "Look inside your attic for daylight, wet insulation, or water trails after rain.",
  "Inspect ceilings and upper walls for fresh brown stains or bubbling paint.",
  "Photograph everything and note the storm date — it helps your claim.",
];

const STEPS = [
  { icon: Search, title: "Free Storm Damage Inspection", body: "We perform a thorough, no-cost inspection and fully document the damage with photos, measurements, and a written report." },
  { icon: FileText, title: "File Your Claim", body: "Using our documentation, you file the claim with your insurance company. We'll walk you through exactly what to say." },
  { icon: Users, title: "We Meet Your Adjuster", body: "We meet your insurance adjuster on-site to point out every area of damage and advocate for a full, fair scope of work." },
  { icon: ClipboardCheck, title: "Approval & Scope Review", body: "Once approved, we review the insurer's scope and estimate with you so you understand your coverage and any deductible." },
  { icon: Hammer, title: "Materials & Installation", body: "Pick your CertainTeed color, then our crews complete the replacement with flat-rate pricing and a tidy daily clean-up." },
  { icon: CheckCircle2, title: "Final Inspection & Warranty", body: "We do a final walkthrough and register your 50-year CertainTeed guarantee. Your home is protected for the long haul." },
];

const HELP = [
  "We document every bit of storm and wind damage the right way",
  "We meet your adjuster on-site so nothing gets missed",
  "We handle the paperwork and coordinate the scope",
  "In most approved claims, you pay only your deductible",
  "No surprise charges — honest, flat-rate pricing throughout",
  "Locally owned, licensed & insured in Washington (WA Lic. #HONEYBE762PG)",
];

const INSURANCE_FAQS = [
  { q: "How much will a roof insurance claim cost me out of pocket?", a: "For an approved storm or wind damage claim, you typically pay only your policy deductible — your insurer covers the rest of the approved scope. We review the numbers with you up front so there are no surprises." },
  { q: "How long do I have to file a storm damage claim?", a: "Most homeowner policies require you to file within a set window after the damage (often one year, sometimes less). Don't wait — schedule a free inspection soon after a storm so damage is documented while it's fresh." },
  { q: "Do you meet with my insurance adjuster?", a: "Yes. We meet your adjuster on-site to walk the roof together, point out all storm-related damage, and advocate for a complete, fair scope of work so you aren't left under-covered." },
  { q: "What if my insurance claim is denied?", a: "A denial isn't always final. We can provide additional documentation, request a re-inspection, and help you understand your options. Our detailed reports often make the difference." },
  { q: "Will a weather-related claim raise my rates?", a: "Storm and wind damage are generally treated as acts of nature rather than homeowner fault, but every policy is different. We recommend confirming with your insurer — and we're happy to help you gather the documentation you need." },
];

function InsuranceSeo() {
  useEffect(() => {
    const title = "Roof Insurance Claims & Wind Damage Help | HoneyBee Roofers, WA";
    document.title = title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = "Storm & wind damage roof insurance claims made simple. Learn what roof wind damage looks like, what to check after a storm, and how Honey Bee Roofing guides you through the entire insurance claim process in Snohomish, King & Skagit County, WA.";
    setMeta("name", "description", desc);
    setMeta("name", "keywords", "roof damage insurance claim help, wind damage roof repair, storm damage roof replacement insurance, roof insurance claim Washington, hail damage roof claim, roof adjuster meeting");
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", `${SITE_URL}${PAGE_PATH}`);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.setAttribute("rel", "canonical"); document.head.appendChild(canonical); }
    canonical.setAttribute("href", `${SITE_URL}${PAGE_PATH}`);

    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: INSURANCE_FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    };
    const howToLd = {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: "How the Roof Insurance Claim Process Works",
      step: STEPS.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.body })),
    };
    let script = document.getElementById("ld-json");
    if (!script) { script = document.createElement("script"); script.id = "ld-json"; script.type = "application/ld+json"; document.head.appendChild(script); }
    script.textContent = JSON.stringify([faqLd, howToLd]);
  }, []);
  return null;
}

export default function InsurancePage() {
  const [modal, setModal] = useState(false);
  const openQuote = () => setModal(true);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-obsidian min-h-screen">
      <InsuranceSeo />
      <Header onQuote={openQuote} />

      {/* Hero */}
      <section id="top" className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.storm} alt="Storm and wind damaged roof in Washington" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/50" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Link to="/" data-testid="back-home" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-honey mb-5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-honey/30 bg-honey/10 mb-5 mx-auto">
            <Wind className="w-3.5 h-3.5 text-honey" />
            <span className="text-xs font-semibold tracking-wide text-honey">Storm & Wind Damage Specialists</span>
          </div>
          <h1 className="font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tighter">
            Roof Insurance Claims, <span className="text-honey">Made Simple</span>
          </h1>
          <p className="text-lg text-zinc-300 mt-6 max-w-2xl mx-auto leading-relaxed">
            A windstorm can damage your roof in minutes — and the insurance process shouldn't add to the stress. We document the
            damage, meet your adjuster, and guide you from inspection to a brand-new, fully-warrantied roof.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <button onClick={openQuote} data-testid="insurance-quote-btn"
              className="bg-honey text-white font-bold px-7 py-4 rounded-sm hover:bg-honey-600 transition-colors w-full sm:w-auto">
              Get a Free Storm Inspection
            </button>
            <a href={`tel:${COMPANY.phoneRaw}`} data-testid="insurance-call-btn"
              className="border border-white/20 text-white font-semibold px-7 py-4 rounded-sm hover:border-honey hover:text-honey transition-colors flex items-center gap-2 w-full sm:w-auto justify-center">
              <Phone className="w-4 h-4 text-honey" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Signs of wind damage */}
      <section className="py-24 honeycomb-bg">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">What Wind Damage Looks Like</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              6 Signs Your Roof Took a Hit
            </h2>
            <p className="text-zinc-400 mt-4 text-lg">
              Wind and storm damage isn't always obvious from the curb. Here's what our inspectors look for — and what you can
              watch for too.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SIGNS.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
                data-testid={`sign-${i}`}
                className="p-6 rounded-md border border-white/10 bg-carbon hover:border-honey/40 transition-colors">
                <div className="w-11 h-11 rounded-sm bg-honey/15 border border-honey/30 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-honey" />
                </div>
                <h3 className="text-lg font-display font-bold text-white">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What to look for checklist */}
      <section className="py-24 bg-carbon border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-md overflow-hidden border border-white/10">
            <img src={IMAGES.inspection} alt="Roofer inspecting storm damage after a windstorm" className="w-full h-[420px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-carbon/80 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 bg-black/60 backdrop-blur-md border border-white/10 rounded-sm p-4 flex items-center gap-3">
              <Camera className="w-5 h-5 text-honey shrink-0" />
              <p className="text-sm text-zinc-200">Snap photos and note the storm date — documentation strengthens your claim.</p>
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">After the Storm</p>
            <h2 className="text-3xl sm:text-4xl font-display font-black uppercase tracking-tighter text-white">
              What to Look For
            </h2>
            <ul className="mt-7 space-y-4">
              {CHECKLIST.map((c) => (
                <li key={c} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="w-5 h-5 text-honey mt-0.5 shrink-0" />
                  <span className="text-[15px] leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-zinc-500 mt-6">Not sure what you're seeing? We'll take a look for free and tell you honestly whether you have a claim.</p>
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="py-24 bg-obsidian">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">Step by Step</p>
            <h2 className="text-4xl sm:text-5xl font-display font-black uppercase tracking-tighter text-white">
              The Insurance Claim Process
            </h2>
            <p className="text-zinc-400 mt-4 text-lg">
              From the first inspection to your final warranty, here's exactly how we guide you through an insurance roof replacement.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.06, duration: 0.5 }}
                data-testid={`step-${i}`}
                className="relative p-6 rounded-md border border-white/10 bg-carbon">
                <span className="absolute top-5 right-5 text-5xl font-display font-black text-white/5">{i + 1}</span>
                <div className="w-11 h-11 rounded-sm bg-honey/15 border border-honey/30 flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5 text-honey" />
                </div>
                <h3 className="text-lg font-display font-bold text-white">{s.title}</h3>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How we help */}
      <section className="py-24 bg-carbon border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-3">You're Not Alone</p>
            <h2 className="text-3xl sm:text-4xl font-display font-black uppercase tracking-tighter text-white">
              How HoneyBee Helps With Your Claim
            </h2>
            <ul className="mt-7 space-y-4">
              {HELP.map((h) => (
                <li key={h} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="w-5 h-5 text-honey mt-0.5 shrink-0" />
                  <span className="text-[15px] leading-relaxed">{h}</span>
                </li>
              ))}
            </ul>
            <button onClick={openQuote} data-testid="insurance-help-cta"
              className="mt-8 bg-honey text-white font-bold px-7 py-4 rounded-sm hover:bg-honey-600 transition-colors">
              Start My Free Inspection
            </button>
          </div>
          <div className="bg-obsidian border border-white/10 rounded-md p-6 sm:p-8">
            <h3 className="text-2xl font-display font-bold text-white">Free Storm Damage Inspection</h3>
            <p className="text-sm text-zinc-400 mt-1 mb-5">Tell us what happened — we'll take it from there.</p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <FAQ items={INSURANCE_FAQS} title="Insurance Claim FAQs" eyebrow="Common Questions" />
      <CTABand onQuote={openQuote} />
      <Footer />
      <StickyCallBar onQuote={openQuote} />

      {modal && (
        <div data-testid="quote-modal" onClick={() => setModal(false)}
          data-lenis-prevent className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
            className="relative bg-carbon border border-white/10 rounded-md p-6 sm:p-8 w-full max-w-md my-8">
            <button onClick={() => setModal(false)} data-testid="quote-modal-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-display font-bold text-white mb-1">Free Storm Damage Inspection</h2>
            <p className="text-sm text-zinc-400 mb-5">Fixed, no-hassle pricing in 30 seconds.</p>
            <QuoteForm onSuccess={() => {}} />
          </motion.div>
        </div>
      )}
    </div>
  );
}
